import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { db } from '@/modules/shared/lib/db-client';
import { setObservabilityUser, clearObservabilityUser, log, pushEvent, reportError } from '@/modules/shared/lib/observability';
import { authClient, type AuthUser as CognitoUser } from '@/modules/shared/lib/auth-client';

// Minimal User/Session shape kept for code that still destructures `user.id`,
// `user.email`, `user.user_metadata`. Drop-in replacement for the supabase-js
// types we no longer import.
export interface User {
  id: string;
  email: string | null;
  user_metadata?: {
    full_name?: string;
    company_name?: string;
    phone?: string;
  };
}

export interface Session {
  user: User;
  access_token: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data: { user: User | null; session: Session | null } | null }>;
  signUp: (email: string, password: string, metadata?: { full_name?: string; company_name?: string; phone?: string }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function cognitoToUser(c: CognitoUser): User {
  return {
    id: c.id,
    email: c.email,
    user_metadata: { full_name: c.fullName || undefined },
  };
}

// Helper function to link pending team member on first login
async function linkPendingTeamMember(user: User) {
  if (!user.email) return;

  try {
    const { data: pendingMember } = await db
      .from("professionals")
      .select("id, role")
      .eq("professional_type", "operacional")
      .ilike("email", user.email.trim())
      .eq("invite_status", "pending")
      .maybeSingle();

    if (pendingMember) {
      log.debug("Auto-linking pending team member", { detail: String(pendingMember.id) });

      await db
        .from("professionals")
        .update({
          user_id: user.id,
          status: "active",
          invite_status: "accepted",
          last_activity_at: new Date().toISOString(),
        } as any)
        .eq("id", pendingMember.id);

      const validAppRoles = ["admin", "manager", "client_manager", "account_manager", "agile_master", "business_partner", "finance", "tech_recruiter", "tech_lead", "marketing", "product_owner", "coordenador_rh"];
      if (validAppRoles.includes(pendingMember.role)) {
        await db
          .from("user_roles")
          .upsert({
            user_id: user.id,
            role: pendingMember.role as any,
          } as any, { onConflict: "user_id" });
      }

      const { data: memberData } = await db
        .from("professionals")
        .select("pending_permissions")
        .eq("id", pendingMember.id)
        .single();

      const pendingPermissions = (memberData?.pending_permissions as any[] || []);
      if (pendingPermissions.length > 0) {
        const userPermissions = pendingPermissions.map((p: any) => ({
          user_id: user.id,
          feature_key: p.feature_key,
          is_enabled: p.is_enabled,
        }));

        await db
          .from("user_permissions")
          .upsert(userPermissions as any, { onConflict: "user_id,feature_key" });

        await db
          .from("professionals")
          .update({ pending_permissions: [] as any })
          .eq("id", pendingMember.id);
      }

      return true;
    }
  } catch (error) {
    reportError(error, "auto-link-team-member");
    log.error("Error auto-linking team member", {}, error);
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authClient.getMe().then((cognitoUser) => {
      if (cognitoUser) {
        const u = cognitoToUser(cognitoUser);
        setUser(u);
        setSession({ user: u, access_token: "" });
        setObservabilityUser({ id: u.id, email: u.email || undefined });
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    const result = await authClient.login(email, password);
    if (result.error) {
      return { error: new Error(result.error), data: null };
    }
    const u = cognitoToUser(result.user);
    setUser(u);
    setSession({ user: u, access_token: "" });
    setObservabilityUser({ id: u.id, email: u.email || undefined });
    pushEvent("flow.login.success", { method: "password" });

    // Auto-link any pending team invitation and ensure org membership
    setTimeout(async () => {
      linkPendingTeamMember(u);
      try {
        const { data: existingMember } = await db
          .from("organization_members")
          .select("id")
          .eq("user_id", u.id)
          .maybeSingle();
        if (!existingMember) {
          await db.functions.invoke('create-org-on-signup', {
            body: {
              full_name: u.user_metadata?.full_name,
              company_name: u.user_metadata?.company_name,
              phone: u.user_metadata?.phone,
            },
          });
        }
      } catch (e) {
        reportError(e, "ensure-org-on-signup");
      }
    }, 0);

    return { error: null, data: { user: u, session: { user: u, access_token: "" } } };
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: { full_name?: string; company_name?: string; phone?: string }
  ) => {
    const result = await authClient.signup(email, password, {
      fullName: metadata?.full_name,
      companyName: metadata?.company_name,
      phone: metadata?.phone,
    });
    return { error: result.error ? new Error(result.error) : null };
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    clearObservabilityUser();
    await authClient.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
